import { clamp, std } from './math';
import { centsOffNearestSemitone, stabilityScore } from './metrics';
import { freqToMidi } from './note';

import type {
  BreathSegment,
  BreathSegmentId,
  PitchRegister,
  PitchRegisterId,
  PitchSample,
  RangeInfo,
} from './types';

/**
 * segments — the take cut into stretches, so the report can say *when* the voice
 * wavered (breath) and *where in the range* it wavered (pitch registers) instead of
 * only how much it wavered overall.
 *
 * A single steadiness number cannot tell a singer who starts shaky and settles from
 * one who starts well and runs out of air; those two want opposite advice. The whole
 * point of the section is the shape over time.
 *
 * Framework-free and pure — vitest target.
 */

/**
 * Where the breath section cuts the take (seconds from the first reading).
 *
 * The three stretches are the design's (`1000:8628` — 시작 0~3초 / 중간 3~7초 /
 * 후반 7~10초), not a derivation: they exist so the copy beside each score
 * ("안정적으로 시작했어요") lines up with what the singer felt. This array is the only
 * place they are written down — a screen that repeats `0~3초` in its own string will
 * drift the day the design moves the boundary.
 */
export const BREATH_SEGMENTS: readonly {
  id: BreathSegmentId;
  fromSec: number;
  toSec: number;
}[] = [
  { id: 'start', fromSec: 0, toSec: 3 },
  { id: 'middle', fromSec: 3, toSec: 7 },
  { id: 'late', fromSec: 7, toSec: 10 },
];

/**
 * Readings a stretch needs before it gets a score. Steadiness is a *spread*, and the
 * spread of a handful of readings is mostly luck — at the production frame rate 16
 * readings is about a third of a second of held tone. Under that the stretch is
 * absent rather than scored: "the take barely reached here" is not "the voice was
 * unsteady here".
 */
const MIN_READINGS = 16;

export const breathSegments = (
  samples: PitchSample[],
  /**
   * The cuts to use. Defaults to the design's three, which is what the diagnosis
   * screen wants; a consumer whose take is not 10 seconds long (landing, admin)
   * passes its own instead of getting stretches its recording never reaches.
   */
  cuts: typeof BREATH_SEGMENTS = BREATH_SEGMENTS,
): BreathSegment[] => {
  if (samples.length === 0) {
    return [];
  }
  // Timestamps are device uptime, so the take's own first reading is second zero.
  const origin = samples[0].tMs;

  return cuts.flatMap(({ id, fromSec, toSec }) => {
    const inside = samples.filter((s) => {
      const sec = (s.tMs - origin) / 1000;
      return sec >= fromSec && sec < toSec;
    });
    if (inside.length < MIN_READINGS) {
      return [];
    }
    const cents = centsOffNearestSemitone(inside.map((s) => s.f0Hz));
    return [{ id, score: Math.round(stabilityScore(cents)) }];
  });
};

/**
 * Where the pitch section cuts the voice, as fractions of the measured range
 * (design §8.5). Fractions and not fixed notes: the design's own example splits a
 * G2~E5 voice at G3 / D#4 / G4 / D5, and those cuts are that singer's passaggio —
 * pinning them to MIDI numbers would put a soprano's whole range inside 저역 확장.
 *
 * The five numbers reproduce the design's example exactly (see spec). They are
 * boundaries of a proportion, so they say nothing about anyone's vocal anatomy —
 * a rough map, not a diagnosis, which is why the screen calls the axis 안정성.
 */
export const PITCH_REGISTERS: readonly {
  id: PitchRegisterId;
  from: number;
  to: number;
}[] = [
  { id: 'lowExtension', from: 0, to: 0.39 },
  { id: 'modal', from: 0.39, to: 0.63 },
  { id: 'passaggio', from: 0.63, to: 0.75 },
  { id: 'head', from: 0.75, to: 0.96 },
  { id: 'highExtension', from: 0.96, to: 1 },
];

/**
 * Longest hole between two readings that still counts as one continuous phrase (ms).
 * Steadiness here is read from the step between consecutive readings, and a step
 * taken across a breath is not a step the voice made — joined, a singer who paused
 * and came back on a different note looks like a singer whose voice cracked.
 *
 * A non-positive gap is dropped for the same reason: callers hand this readings
 * gathered from several takes, and two readings that did not follow each other in
 * time have no step between them.
 */
const MAX_STEP_MS = 60;

/**
 * Steps a register needs before it gets a score, and the spread that scores zero.
 *
 * 16 steps is about a third of a second of singing. 50 cents of spread between
 * consecutive frames is half a semitone of movement *per frame* — at that point the
 * voice is not moving, it is jumping. A smooth slide has an almost constant step and
 * so scores high, which is right: sliding under control is steady.
 */
const MIN_STEPS = 16;
const ZERO_SPREAD_CENTS = 50;

/**
 * The voice's range cut into five registers, each scored on how controlled it was
 * there. `range` is null when the session never measured the low and high ends —
 * without it there is nothing to divide, so there is nothing to say.
 */
export const pitchRegisters = (
  samples: PitchSample[],
  range: RangeInfo | null,
  /** The fractions to cut at. Defaults to the design's five (see `PITCH_REGISTERS`). */
  cuts: typeof PITCH_REGISTERS = PITCH_REGISTERS,
): PitchRegister[] => {
  if (!range) {
    return [];
  }
  const low = Math.round(freqToMidi(range.lowHz));
  const high = Math.round(freqToMidi(range.highHz));
  const span = high - low;
  if (span <= 0) {
    return [];
  }
  const cut = (fraction: number): number => Math.ceil(low + fraction * span);

  const bounds = cuts.map(({ id, from, to }, i) => ({
    id,
    lowMidi: cut(from),
    highMidi: i === cuts.length - 1 ? high : cut(to) - 1,
  }));

  const midis = samples.map((s) => freqToMidi(s.f0Hz));
  // Which register each reading falls in, once. The bands are contiguous and do not
  // overlap, so a reading has exactly one — asking each register about every reading
  // instead walks the take five times to learn the same thing.
  const at = midis.map((m) =>
    bounds.findIndex((b) => m >= b.lowMidi - 0.5 && m < b.highMidi + 0.5),
  );

  // Steps between neighbouring readings that sit in the SAME register and were close
  // enough in time to be the same phrase.
  const steps: number[][] = bounds.map(() => []);
  for (let j = 1; j < samples.length; j++) {
    const dtMs = samples[j].tMs - samples[j - 1].tMs;
    if (at[j] < 0 || at[j] !== at[j - 1] || dtMs <= 0 || dtMs > MAX_STEP_MS) {
      continue;
    }
    steps[at[j]].push((midis[j] - midis[j - 1]) * 100);
  }

  return bounds.map((bound, i) =>
    steps[i].length < MIN_STEPS
      ? bound
      : {
          ...bound,
          score: Math.round(
            clamp(100 - (100 * std(steps[i])) / ZERO_SPREAD_CENTS),
          ),
        },
  );
};
